import { AsyncPipe } from '@angular/common'
import { Component, Input, OnInit, inject } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { NgbActiveModal, NgbDateStruct, NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap'
import { NgSelectModule } from '@ng-select/ng-select'
import { EditorModule } from '@tinymce/tinymce-angular'
import { NgxPermissionsModule } from 'ngx-permissions'
import { ToastrService } from 'ngx-toastr'
import { Observable } from 'rxjs'
import { SmartQuoteService } from 'src/app/core/services/smartquote/smartquote.service'
import { SupplierService } from 'src/app/core/services/suppliers/supplier.service'
import { CalloutComponent } from 'src/app/shared/components/callout/callout/callout.component'
import { ButtonComponent } from 'src/app/shared/components/form-elements/button/button.component'
import { InputComponent } from 'src/app/shared/components/form-elements/input/input.component'
import { LabelComponent } from 'src/app/shared/components/form-elements/label/label.component'
import { ListOptionDirective } from 'src/app/shared/components/list-selector/list-selector/list-option.directive'
import { ListSelectorComponent } from 'src/app/shared/components/list-selector/list-selector/list-selector.component'
import { PaginationDetailComponent } from 'src/app/shared/components/pagination-detail/pagination-detail.component'
import { PaginatedList } from 'src/app/shared/interfaces/api-response/api-paginated-list.interface'
import { SendSmartQuoteToSupplierRequest } from 'src/app/shared/interfaces/smartquote/send-smart-quote-to-supplier-request.interface'
import { SmartQuoteEmailTemplate } from 'src/app/shared/interfaces/smartquote/smart-quote-email-template.interface'
import { SupplierEmailFilter } from 'src/app/shared/interfaces/suppliers/supplier-email-filter.interface'
import { SupplierEmail } from 'src/app/shared/interfaces/suppliers/supplier-email.interface'
import { tinymceSettings } from 'src/app/shared/tinymce-settings'

@Component({
    selector: 'sf-smartquote-send-order-modal',
    imports: [
        ReactiveFormsModule,
        EditorModule,
        NgSelectModule,
        NgbDatepickerModule,
        NgxPermissionsModule,
        CalloutComponent,
        PaginationDetailComponent,
        LabelComponent,
        InputComponent,
        ListSelectorComponent,
        ListOptionDirective,
        ButtonComponent,
        AsyncPipe,
    ],
    templateUrl: './smartquote-send-order-modal.component.html',
    styleUrl: './smartquote-send-order-modal.component.scss',
})
export class SmartquoteSendOrderModalComponent implements OnInit {
    activeModal = inject(NgbActiveModal)
    fb = inject(FormBuilder)
    smartQuoteService = inject(SmartQuoteService)
    supplierService = inject(SupplierService)
    toastr = inject(ToastrService)

    tinymceSettings = tinymceSettings

    @Input()
    smartQuoteId!: number

    emailTemplates$!: Observable<SmartQuoteEmailTemplate[]>

    supplierEmailsFilter: SupplierEmailFilter = {
        pageNumber: 1,
        pageSize: 10,
    }
    supplierEmails$!: Observable<PaginatedList<SupplierEmail>>
    selectedSupplierEmail: SupplierEmail | undefined

    form!: SmartquoteSendOrderForm
    hasSelectedATemplate = false
    showDeliveryDateControl = false
    minDate!: NgbDateStruct

    ngOnInit() {
        this.emailTemplates$ = this.smartQuoteService.getSmartquoteEmailTemplates(this.smartQuoteId)
        this.loadSupplierEmails()
        const today = new Date()
        today.setDate(today.getDate() + 1)

        this.minDate = { year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate() }

        this.form = this.fb.group({
            emailAddress: this.fb.nonNullable.control('', [Validators.required, Validators.email]),
            subject: this.fb.nonNullable.control('', [Validators.required, Validators.maxLength(500)]),
            heading: this.fb.nonNullable.control('', [Validators.maxLength(200)]),
            body: this.fb.nonNullable.control('', [Validators.required, Validators.maxLength(20000)]),
            deliveryDate: this.fb.control<string | null>(null),
        })
    }

    loadSupplierEmails(): void {
        this.supplierEmails$ = this.supplierService.getSupplierEmails(this.supplierEmailsFilter)
    }

    onSupplierEmailPageChanged(page: number): void {
        this.supplierEmailsFilter = {
            ...this.supplierEmailsFilter,
            pageNumber: page,
        }
        this.loadSupplierEmails()
    }

    onEmailTemplateChange(template: SmartQuoteEmailTemplate) {
        this.hasSelectedATemplate = true
        this.form.controls.body.patchValue(template.body)
        this.form.controls.subject.patchValue(template.subject)
        this.form.controls.heading.patchValue(template.heading)

        if (template.forDelivery) {
            this.form.controls.deliveryDate.addValidators(Validators.required)
            this.showDeliveryDateControl = true
        } else {
            this.form.controls.deliveryDate.clearValidators()
            this.showDeliveryDateControl = false
        }
        this.form.controls.deliveryDate.updateValueAndValidity()
    }

    onSupplierChange(supplierEmail: SupplierEmail) {
        this.selectedSupplierEmail = supplierEmail
        this.form.controls.emailAddress.patchValue(supplierEmail.emailAddress)
    }

    sendTheOrder(): void {
        if (this.form.invalid) {
            this.toastr.error('Please fill in all required fields')
            return
        }

        const request: SendSmartQuoteToSupplierRequest = this.form.value as SendSmartQuoteToSupplierRequest

        this.smartQuoteService.sendSmartQuoteToSupplier(this.smartQuoteId, request).subscribe({
            next: () => {
                this.toastr.success('Order sent successfully')
                this.activeModal.close()
            },
            error: () => {
                this.toastr.error('Failed to send order')
            },
        })
    }
}

interface SmartquoteSendOrderForm
    extends FormGroup<{
        emailAddress: FormControl<string>
        subject: FormControl<string>
        heading: FormControl<string>
        body: FormControl<string>
        deliveryDate: FormControl<string | null>
    }> {}
